import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useState } from 'react';
import { addDays, addMonths, isSameMonth, startOfMonth } from 'date-fns';
import styles from '../styles.css.js';
import useFocusVisible from '../../internal/hooks/focus-visible/index.js';
import CalendarHeader from './header';
import Grid from './grid';
import moveFocusHandler from './utils/move-focus-handler';
import { useUniqueId } from '../../internal/hooks/use-unique-id/index.js';
import { formatDate, memoizedDate } from './utils/date.js';
import { useEffectOnUpdate } from '../../internal/hooks/use-effect-on-update.js';
var Calendar = function (_a) {
    var locale = _a.locale, startOfWeek = _a.startOfWeek, displayedDate = _a.displayedDate, todayAriaLabel = _a.todayAriaLabel, selectedDate = _a.selectedDate, isDateEnabled = _a.isDateEnabled, onChangeMonth = _a.onChangeMonth, onSelectDate = _a.onSelectDate, previousMonthLabel = _a.previousMonthLabel, nextMonthLabel = _a.nextMonthLabel;
    var focusVisible = useFocusVisible();
    var headerId = useUniqueId('calendar-dialog-title-');
    var elementRef = useRef(null);
    var gridWrapperRef = useRef(null);
    var _b = useState(null), focusedDate = _b[0], setFocusedDate = _b[1];
    var selectFocusedDate = function (selected, baseDate) {
        if (selected && isDateEnabled(selected) && isSameMonth(selected, baseDate)) {
            return selected;
        }
        var today = new Date();
        if (isDateEnabled(today) && isSameMonth(today, baseDate)) {
            return today;
        }
        if (isDateEnabled(baseDate)) {
            return baseDate;
        }
        return null;
    };
    var getBaseDate = function (date) {
        var startDate = startOfMonth(date);
        if (isDateEnabled(startDate)) {
            return startDate;
        }
        return moveFocusHandler(startDate, isDateEnabled, function (date) { return addDays(date, 1); });
    };
    var baseDate = getBaseDate(displayedDate);
    var focusedOrSelectedDate = focusedDate || selectFocusedDate(selectedDate, baseDate);
    var onHeaderChangeMonthHandler = function (isPrevious) {
        onChangeMonth(addMonths(baseDate, isPrevious ? -1 : 1));
        setFocusedDate(null);
    };
    var onGridChangeMonthHandler = function (newMonth) {
        onChangeMonth(newMonth);
        setFocusedDate(null);
    };
    var onGridFocusDateHandler = function (_a) {
        var date = _a.date;
        if (date) {
            var value = memoizedDate('focused', formatDate(date));
            setFocusedDate(value);
        }
    };
    var onGridSelectDateHandler = function (detail) {
        onSelectDate(detail);
        setFocusedDate(null);
    };
    // The focused date changes as a feedback to keyboard navigation in the grid.
    // Once changed, the corresponding day button needs to receive the actual focus.
    useEffectOnUpdate(function () {
        var _a, _b;
        if (focusedDate) {
            (_b = (_a = elementRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(".".concat(styles['calendar-day-focusable']))) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [focusedDate]);
    var onGridBlur = function (event) {
        var _a;
        var newFocusTargetIsInGrid = event.relatedTarget && ((_a = gridWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget));
        if (!newFocusTargetIsInGrid) {
            setFocusedDate(null);
        }
    };
    return (React.createElement("div", __assign({}, focusVisible, { className: styles.calendar, tabIndex: 0, role: "application", "aria-describedby": headerId, ref: elementRef }),
        React.createElement("div", { className: styles['calendar-inner'] },
            React.createElement(CalendarHeader, { headerId: headerId, baseDate: baseDate, locale: locale, onChangeMonth: onHeaderChangeMonthHandler, previousMonthLabel: previousMonthLabel, nextMonthLabel: nextMonthLabel }),
            React.createElement("div", { onBlur: onGridBlur, ref: gridWrapperRef },
                React.createElement(Grid, { locale: locale, baseDate: baseDate, isDateEnabled: isDateEnabled, focusedDate: focusedOrSelectedDate, onSelectDate: onGridSelectDateHandler, onFocusDate: onGridFocusDateHandler, onChangeMonth: onGridChangeMonthHandler, startOfWeek: startOfWeek, todayAriaLabel: todayAriaLabel, selectedDate: selectedDate, handleFocusMove: moveFocusHandler })))));
};
export default Calendar;
//# sourceMappingURL=index.js.map